import React, { MouseEventHandler, useCallback } from "react";
import clsx from "clsx";

import {
  HvBaseSwitch,
  HvFormElement,
  HvLabel,
  HvSwitchProps,
  HvTypography,
  HvWarningText,
  useControlled,
} from "@hitachivantara/uikit-react-core";

import { setId, useUniqueId } from "lib/utils/ids";

import useStyles from "./styles";

const SwitchLabel = ({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick?: MouseEventHandler<HTMLElement> | undefined;
}) => {
  const variant = disabled ? "placeholderText" : "normalText";
  const style = disabled ? undefined : { cursor: "pointer" };

  return (
    <HvTypography
      aria-hidden="true"
      variant={variant}
      style={style}
      onClick={onClick}
    >
      {label}
    </HvTypography>
  );
};

interface SwitchWithLabelsProps extends HvSwitchProps {
  offLabel?: string;
  onLabel?: string;
}

const SwitchWithLabels = ({
  className,

  id,
  name,
  value = "on",
  required = false,
  readOnly = false,
  disabled = false,

  label,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  labelProps,

  offLabel,
  onLabel,

  checked,
  defaultChecked = false,

  onChange,

  status,
  statusMessage,
  "aria-errormessage": ariaErrorMessage,

  inputProps,

  ...others
}: SwitchWithLabelsProps): JSX.Element => {
  const classes = useStyles();

  const elementId = useUniqueId(id, "hvswitch");

  const [isChecked, setIsChecked] = useControlled(
    checked,
    Boolean(defaultChecked)
  );

  const [validationState, setValidationState] = useControlled(
    status,
    "standBy"
  );

  const [validationMessage] = useControlled(statusMessage, "Required");

  const onLocalChange = useCallback(
    (evt, newChecked) => {
      setIsChecked(() => {
        // this will only run if uncontrolled
        if (required && !newChecked) {
          setValidationState("invalid");
        } else {
          setValidationState("valid");
        }

        return newChecked;
      });

      onChange?.(evt, newChecked, value);
    },
    [onChange, required, setIsChecked, setValidationState, value]
  );

  const clickCallback = disabled
    ? undefined
    : (evt: React.MouseEvent<HTMLElement>) => onLocalChange(evt, !isChecked);

  // the error message area will only be created if:
  // - an external element that provides an error message isn't identified via aria-errormessage AND
  //   - both status and statusMessage properties are being controlled OR
  //   - status is uncontrolled and required is true
  const canShowError =
    ariaErrorMessage == null &&
    ((status !== undefined && statusMessage !== undefined) ||
      (status === undefined && required));

  const isStateInvalid = validationState === "invalid";

  let errorMessageId;
  if (isStateInvalid) {
    errorMessageId = canShowError
      ? setId(elementId, "error")
      : ariaErrorMessage;
  }

  return (
    <HvFormElement
      id={id}
      name={name}
      status={validationState}
      disabled={disabled}
      required={required}
      readOnly={readOnly}
      className={clsx(className, classes.root)}
    >
      {label && (
        <HvLabel
          id={setId(elementId, "label")}
          htmlFor={setId(elementId, "input")}
          label={label}
          className={clsx(classes.label)}
          {...labelProps}
        />
      )}
      <div
        className={clsx(classes.switchContainer, {
          [classes.invalidSwitch]: isStateInvalid,
        })}
      >
        {offLabel && (
          <SwitchLabel
            label={offLabel}
            disabled={disabled}
            onClick={clickCallback}
          />
        )}
        <HvBaseSwitch
          id={label ? setId(elementId, "input") : setId(id, "input")}
          name={name}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          onChange={onLocalChange}
          value={value}
          checked={isChecked}
          inputProps={{
            "aria-invalid": isStateInvalid ? true : undefined,
            "aria-errormessage": errorMessageId,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledBy,
            "aria-describedby": ariaDescribedBy,
            ...inputProps,
          }}
          {...others}
        />
        {onLabel && (
          <SwitchLabel
            label={onLabel}
            disabled={disabled}
            onClick={clickCallback}
          />
        )}
      </div>
      {canShowError && (
        <HvWarningText
          id={setId(elementId, "error")}
          className={clsx(classes.error)}
          disableBorder
          disableAdornment
          hideText
        >
          {validationMessage}
        </HvWarningText>
      )}
    </HvFormElement>
  );
};

export default SwitchWithLabels;
